import {IconButton, Tooltip, useTranslationContext} from 'platform/components';
import {HStack, Show} from 'platform/foundation';

import {type RefObject} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {INLINE_EDGE_INSET} from '../../constants/INLINE_EDGE_INSET';
import {useDataGridContext} from '../../context/useDataGridContext';
import {useEagGridConfig} from '../../context/useEagGridConfig';
import {useIsInlineDesktop} from '../../hooks/useIsInlineDesktop';
import {useIsMobileView} from '../../hooks/useIsMobileView';
import {usePresetState} from '../../hooks/usePresetState';
import {
  useSmartSearchEnabled,
  usePresetsEnabled,
  usePresetDisplayMode,
  useGlobalActionsEnabled,
  useFilterToolPanelVisible,
  useColumnToolPanelVisible,
  useSettingsToolPanelVisible,
  useGridHasCurrentTotalCount,
  useGridTotalCount,
  useExportAllowed,
} from '../../store/selectors';
import {
  FeGridSettings,
  GetDataQueryResponse,
  TransformedDefinitionApiResponse,
} from '../../types/Api';
import {EagColumnState} from '../../types/EagColumnState';
import {ToolpanelType} from '../../types/ToolpanelType';
import {getPresetStates} from '../../utils/getPresetStates';
import {refreshDatagrid} from '../../utils/refreshDatagrid';
import {MaximizeGridButton} from '../GridMaximization/MaximizeGridButton';
import {GridPdfExportButton} from '../GridPdfExportButton/GridPdfExportButton';
import {PresetDropdownRenderer} from '../PresetPanel/PresetDropdownRenderer';
import {PresetsPanelRenderer} from '../PresetPanel/PresetsPanelRenderer';
import {PresetTabsScrollRenderer} from '../PresetPanel/PresetTabsScrollRenderer';
import {ExportButton} from './components/ExportButton/ExportButton';
import {FindColumnButton} from './components/FindColumn/FindColumnButton';
import {
  FilterCountBadge,
  PresetStripRow,
  ToolbarDivider,
} from './components/GridHeaderParts/GridHeaderParts';
import {LoadingBar} from './components/LoadingBar/LoadingBar';
import {QuickFilterInput} from './components/QuickFilterInput/QuickFilterInput';
import {SharePresetButton} from './components/SharePresetButton/SharePresetButton';
import {SortDropdownButton} from './components/SortDropdownButton/SortDropdownButton';
import {buildFindColumnEntries} from './utils/buildFindColumnEntries';

type GridHeaderProps = {
  dataGridSettings: FeGridSettings;
  // GridHeader only reads gridCode off dataGridProps (handleRefreshData → refreshDatagrid).
  dataGridProps: {gridCode: string};
  definition: TransformedDefinitionApiResponse;
  dataQuery: GetDataQueryResponse | Nullish;
  refreshData: () => void;
  columnStates: EagColumnState[];
  activePanel: ToolpanelType | Nullish;
  onTogglePanel: (panel: ToolpanelType) => void;
  /** Number of active (non-static) filters; drives the badge on the filter icon button. */
  activeFilterCount: number;
  autoFocusSmartSearch?: boolean;
  maximizationEnabled?: boolean;
  tableRef: RefObject<HTMLTableElement | null>;
} & TestIdProps;

const getPanelPriority = (activePanel: ToolpanelType | Nullish, panel: ToolpanelType) =>
  activePanel === panel ? ('secondary' as const) : undefined;

const DESKTOP_TOOLBAR_CLASS_NAME = 'flex flex-wrap items-start gap-[10px] bg-white';

export function GridHeader(props: GridHeaderProps) {
  const t = useTranslationContext();
  const {buildAbsoluteUrl} = useEagGridConfig();
  const context = useDataGridContext();
  const hasPdfExport = isNotNil(context?.pdfColumns) && isNotNil(context?.onPdfExportCallback);
  const isMobile = useIsMobileView();
  const isInlineDesktop = useIsInlineDesktop();
  const smartSearchEnabled = useSmartSearchEnabled();
  const presetsEnabled = usePresetsEnabled();
  const presetDisplayMode = usePresetDisplayMode();
  const globalActionsEnabled = useGlobalActionsEnabled();
  const exportAllowed = useExportAllowed();
  const filterToolPanelVisible = useFilterToolPanelVisible();
  const columnToolPanelVisible = useColumnToolPanelVisible();
  const settingsToolPanelVisible = useSettingsToolPanelVisible();
  // Total count of the active view — used as the Default tab's badge count while the
  // virtual preset is active (avoids a duplicate /count; see PresetsPanelRenderer).
  const gridTotalCount = useGridTotalCount();
  const hasCurrentTotalCount = useGridHasCurrentTotalCount();

  const showHeaderPanel =
    smartSearchEnabled ||
    presetsEnabled ||
    globalActionsEnabled ||
    hasPdfExport ||
    props.maximizationEnabled;

  const presetState = usePresetState();

  const currentColumnSettings = getPresetStates(props.columnStates);

  const findColumnEntries = buildFindColumnEntries(props.definition);

  const handleCreatePreset = (name: string, desc: string, color?: string) =>
    presetState.createPreset(name, desc, currentColumnSettings, props.dataGridSettings, color);

  const handleUpdatePreset = (presetId: string) =>
    presetState.updatePreset(presetId, currentColumnSettings, props.dataGridSettings);

  if (!showHeaderPanel) {
    return null;
  }

  const handleRefreshData = () => {
    props.refreshData();
    refreshDatagrid(props.dataGridProps.gridCode);
  };

  const presetRendererProps = {
    activePreset: presetState.activePreset,
    isPresetChanged: presetState.isPresetChanged,
    activatePreset: presetState.activatePreset,
    deactivatePreset: presetState.deactivatePreset,
    applyPresetToVirtual: presetState.applyPresetToVirtual,
    deletePreset: presetState.deletePreset,
    duplicatePreset: presetState.duplicatePreset,
    resetOverrides: presetState.resetOverrides,
    updatePreset: handleUpdatePreset,
    createPreset: handleCreatePreset,
    renamePreset: presetState.renamePreset,
    updatePresetPosition: presetState.updatePresetPosition,
    updatePresetPin: presetState.updatePresetPin,
    rowCountGetter: presetState.rowCountGetter,
    presets: presetState.presets,
    virtualPreset: presetState.virtualPreset,
    activeViewRowCount: hasCurrentTotalCount ? gridTotalCount : undefined,
    activeViewIsLoading: !hasCurrentTotalCount,
    'data-testid': props['data-testid'],
  } as const;

  // Tab-based modes render in a dedicated PresetStripRow (row 1).
  const PresetTabRenderer =
    presetDisplayMode === 'TABS_SCROLL' ? PresetTabsScrollRenderer : PresetsPanelRenderer;
  const presetStrip =
    // eslint-disable-next-line eag/prefer-show -- Pre-existing violation
    presetDisplayMode === 'TABS_WRAP' || presetDisplayMode === 'TABS_SCROLL' ? (
      <PresetTabRenderer {...presetRendererProps} />
    ) : null;

  // Dropdown mode renders inline in the toolbar row (row 2), next to quick search.
  const presetDropdown =
    presetDisplayMode === 'DROPDOWN' ? <PresetDropdownRenderer {...presetRendererProps} /> : null; // eslint-disable-line eag/prefer-show -- Pre-existing violation

  // When the presets strip sits above (TABS modes), tighten the toolbar's top
  // padding so the search row hugs the tabs above it (T20-135710). Without a
  // strip (DROPDOWN/NONE) the toolbar is the top row, so keep its full padding,
  // or only INLINE_EDGE_INSET in an INLINE grid on desktop.
  const topRowPaddingTop = isInlineDesktop ? INLINE_EDGE_INSET : 10;
  const toolbarPaddingTop = isNotNil(presetStrip) ? 6 : topRowPaddingTop;

  if (isMobile) {
    // Mobile: same two-row split as desktop — presets strip above the toolbar row.
    // The presets strip scrolls horizontally internally (handled by PresetsPanelRenderer).
    // The toolbar row wraps: the search takes a full line, so the preset dropdown and
    // the icons keep their width on narrow screens. justify-end keeps the second line
    // right-aligned when no preset dropdown fills it.
    return (
      <div className="relative" data-testid={suffixTestId('headerWrapper', props)}>
        {/* Row 1 (mobile): presets strip — only rendered when presets are enabled */}
        <Show when={presetStrip}>
          <PresetStripRow>{presetStrip}</PresetStripRow>
        </Show>
        {/* Row 2 (mobile): search input on its own line, then preset dropdown (if DROPDOWN mode) + icon buttons */}
        <div
          className="flex flex-wrap items-center justify-end gap-2 px-3.5"
          style={{paddingTop: toolbarPaddingTop, paddingBottom: 10}}
        >
          <Show when={smartSearchEnabled}>
            <div className="basis-full min-w-0">
              <QuickFilterInput
                autoFocus={props.autoFocusSmartSearch}
                data-testid={props['data-testid']}
              />
            </div>
          </Show>
          {presetDropdown}
          <Show when={globalActionsEnabled}>
            <HStack justify="flex-end" spacing={1}>
              <IconButton
                aria-label={t('page.datagrid.button.refreshData')}
                icon="navigation/refresh"
                onClick={handleRefreshData}
              />
              <Show when={filterToolPanelVisible}>
                <span className="relative inline-flex">
                  <IconButton
                    aria-label={t('page.datagrid.labels.filters')}
                    icon="content/filter_list"
                    onClick={() => props.onTogglePanel(ToolpanelType.filter)}
                    priority={getPanelPriority(props.activePanel, ToolpanelType.filter)}
                  />
                  <Show when={props.activeFilterCount > 0}>
                    <FilterCountBadge
                      count={props.activeFilterCount}
                      testId={suffixTestId('filterCountBadgeMobile', props)}
                    />
                  </Show>
                </span>
              </Show>
              <SortDropdownButton
                columnStates={props.columnStates}
                definition={props.definition}
                data-testid={props['data-testid']}
              />
              <Show when={columnToolPanelVisible}>
                <FindColumnButton
                  columns={findColumnEntries}
                  columnStates={props.columnStates}
                  tableRef={props.tableRef}
                  data-testid={props['data-testid']}
                />
              </Show>
              <Show when={columnToolPanelVisible}>
                <IconButton
                  aria-label={t('page.datagrid.labels.columnSetting')}
                  icon="editor/table_chart"
                  onClick={() => props.onTogglePanel(ToolpanelType.column)}
                  priority={getPanelPriority(props.activePanel, ToolpanelType.column)}
                />
              </Show>
              <Show when={settingsToolPanelVisible}>
                <IconButton
                  aria-label={t('page.datagrid.labels.settings')}
                  icon="action/settings"
                  onClick={() => props.onTogglePanel(ToolpanelType.settings)}
                  priority={getPanelPriority(props.activePanel, ToolpanelType.settings)}
                />
              </Show>
            </HStack>
          </Show>
          <Show when={hasPdfExport}>
            <GridPdfExportButton data-testid={props['data-testid']} />
          </Show>
          <Show when={props.maximizationEnabled}>
            <MaximizeGridButton data-testid={props['data-testid']} />
          </Show>
        </div>
        <LoadingBar />
      </div>
    );
  }

  return (
    <div className="relative" data-testid={suffixTestId('headerWrapper', props)}>
      {/* Row 1 (desktop): presets strip — full width, only rendered when presets are enabled */}
      <Show when={presetStrip}>
        <PresetStripRow isInlineDesktop={isInlineDesktop}>{presetStrip}</PresetStripRow>
      </Show>
      {/* Row 2 (desktop): search input on the left, icon buttons right-aligned.
          White surface + 1px bottom divider, matching the mock's .toolbar (the
          border is inlined because the repo's prebuilt Tailwind drops border-width
          utilities — same workaround as the preset strip above).
          items-start (not center): every control is 32px tall so a single-line row
          looks identical, but when the DROPDOWN-mode pinned tabs wrap to multiple
          lines the search + icons stay aligned with the FIRST line instead of
          floating to the middle of the grown row.
          An INLINE grid on desktop keeps only INLINE_EDGE_INSET at the left and
          right edges, so the search field and the icons line up with the table. */}
      <div
        className={
          isInlineDesktop ? DESKTOP_TOOLBAR_CLASS_NAME : `${DESKTOP_TOOLBAR_CLASS_NAME} px-3.5`
        }
        style={{
          paddingTop: toolbarPaddingTop,
          paddingInline: isInlineDesktop ? INLINE_EDGE_INSET : undefined,
          paddingBottom: 10,
          borderBottom: '1px solid #EBECF0',
        }}
      >
        <Show when={smartSearchEnabled}>
          <div className="pr-1.5">
            <QuickFilterInput
              autoFocus={props.autoFocusSmartSearch}
              className="w-full max-w-[420px]" // eslint-disable-line react/forbid-component-props -- Pre-existing violation
              data-testid={props['data-testid']}
            />
          </div>
        </Show>
        {presetDropdown}
        <Show when={globalActionsEnabled}>
          {/* grow only when no preset dropdown is rendered: the dropdown renderer is
              flex-1 and already pushes the icons right — a competing grow here would
              steal half the free space and squeeze the pinned tabs into a sliver. */}
          <HStack
            grow={presetDropdown ? undefined : 1}
            justify="flex-end"
            spacing={2}
            align="center"
          >
            <Tooltip label={t('page.datagrid.button.refreshData')}>
              <IconButton
                aria-label={t('page.datagrid.button.refreshData')}
                icon="navigation/refresh"
                onClick={handleRefreshData}
                data-testid={suffixTestId('refreshDataIconButton', props)}
              />
            </Tooltip>
            <Show when={exportAllowed}>
              <ExportButton data-testid={props['data-testid']} />
            </Show>
            <Show when={buildAbsoluteUrl}>
              <SharePresetButton data-testid={props['data-testid']} />
            </Show>
            <Show when={columnToolPanelVisible}>
              <FindColumnButton
                columns={findColumnEntries}
                columnStates={props.columnStates}
                tableRef={props.tableRef}
                data-testid={props['data-testid']}
              />
            </Show>
            <Show
              when={filterToolPanelVisible || columnToolPanelVisible || settingsToolPanelVisible}
            >
              <ToolbarDivider />
            </Show>
            <Show when={filterToolPanelVisible}>
              <Tooltip label={t('page.datagrid.labels.filters')}>
                <span className="relative inline-flex">
                  <IconButton
                    aria-label={t('page.datagrid.labels.filters')}
                    icon="content/filter_list"
                    onClick={() => props.onTogglePanel(ToolpanelType.filter)}
                    priority={getPanelPriority(props.activePanel, ToolpanelType.filter)}
                    data-testid={suffixTestId('toggleFilters', props)}
                  />
                  <Show when={props.activeFilterCount > 0}>
                    <FilterCountBadge
                      count={props.activeFilterCount}
                      testId={suffixTestId('filterCountBadge', props)}
                    />
                  </Show>
                </span>
              </Tooltip>
            </Show>
            <SortDropdownButton
              columnStates={props.columnStates}
              definition={props.definition}
              data-testid={props['data-testid']}
            />
            <Show when={columnToolPanelVisible}>
              <Tooltip label={t('page.datagrid.labels.columnSetting')}>
                <IconButton
                  aria-label={t('page.datagrid.labels.columnSetting')}
                  icon="editor/table_chart"
                  onClick={() => props.onTogglePanel(ToolpanelType.column)}
                  priority={getPanelPriority(props.activePanel, ToolpanelType.column)}
                  data-testid={suffixTestId('toggleColumns', props)}
                />
              </Tooltip>
            </Show>
            <Show when={settingsToolPanelVisible}>
              <Tooltip label={t('page.datagrid.labels.settings')}>
                <IconButton
                  aria-label={t('page.datagrid.labels.settings')}
                  icon="action/settings"
                  onClick={() => props.onTogglePanel(ToolpanelType.settings)}
                  priority={getPanelPriority(props.activePanel, ToolpanelType.settings)}
                  data-testid={suffixTestId('toggleSettings', props)}
                />
              </Tooltip>
            </Show>
          </HStack>
        </Show>
        <Show when={hasPdfExport}>
          <GridPdfExportButton data-testid={props['data-testid']} />
        </Show>
        <Show when={props.maximizationEnabled}>
          <HStack grow={globalActionsEnabled || presetDropdown ? undefined : 1} justify="flex-end">
            <MaximizeGridButton data-testid={props['data-testid']} />
          </HStack>
        </Show>
      </div>
      <LoadingBar />
    </div>
  );
}
